/**
 * Appearance card for the account-wide Space theme default, plus the "Apply to
 * all Spaces" action that clears this alias's per-Space choices.
 */

import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SpaceThemeDefault } from '@adieuu/shared';
import { Card } from '../../components/Card';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useToast } from '../../components/Toast';
import { useTheme } from '../../hooks/useTheme';
import { useOptionalSpacePreferences } from '../../hooks/useSpacePreferences';
import '../../styles/_space-themes-card.scss';

type CountState = { kind: 'loading' } | { kind: 'ready'; count: number } | { kind: 'failed' };

export const SpaceThemesCard = forwardRef<HTMLDivElement>(function SpaceThemesCard(_props, ref) {
  const { t } = useTranslation();
  const toast = useToast();
  const { spaceThemeDefault, setSpaceThemeDefault } = useTheme();
  const spacePrefs = useOptionalSpacePreferences();
  const [changed, setChanged] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [countState, setCountState] = useState<CountState>({ kind: 'loading' });
  const [applying, setApplying] = useState(false);

  const settingLabel =
    spaceThemeDefault === 'space' ? t('spaces.themeSettings.useSpace') : t('spaces.themeSettings.useOwn');

  const chooseDefault = (value: SpaceThemeDefault) => {
    if (value === spaceThemeDefault) return;
    setChanged(true);
    void setSpaceThemeDefault(value);
  };

  const openConfirm = () => {
    if (!spacePrefs) return;
    setCountState({ kind: 'loading' });
    setConfirmOpen(true);
    spacePrefs.getThemeOverrideCount().then(
      (count) => setCountState({ kind: 'ready', count }),
      () => setCountState({ kind: 'failed' }),
    );
  };

  const applyToAll = async () => {
    if (!spacePrefs) return;
    setApplying(true);
    try {
      await spacePrefs.resetThemeOverrides();
      setConfirmOpen(false);
      setChanged(false);
      toast.success(t('spaces.themeSettings.applied'));
    } catch {
      toast.error(t('spaces.themeSettings.applyFailed'));
    } finally {
      setApplying(false);
    }
  };

  const confirmBody =
    countState.kind === 'loading'
      ? t('spaces.themeSettings.confirmChecking')
      : countState.kind === 'failed'
        ? t('spaces.themeSettings.confirmFailed')
        : countState.count === 0
          ? t('spaces.themeSettings.confirmNone', { setting: settingLabel })
          : t('spaces.themeSettings.confirmCount', { count: countState.count, setting: settingLabel });

  const options: Array<{ value: SpaceThemeDefault; label: string; hint: string }> = [
    {
      value: 'space',
      label: t('spaces.themeSettings.useSpace'),
      hint: t('spaces.themeSettings.useSpaceHint'),
    },
    {
      value: 'own',
      label: t('spaces.themeSettings.useOwn'),
      hint: t('spaces.themeSettings.useOwnHint'),
    },
  ];

  return (
    <Card variant="elevated" className="slide-up app-settings-card" ref={ref} data-section="space-themes">
      <h2 className="app-settings-section-title">{t('spaces.themeSettings.title')}</h2>
      <p className="app-settings-section-desc">{t('spaces.themeSettings.description')}</p>

      <fieldset className="app-settings-fieldset space-themes-default">
        <legend className="app-settings-toggle-title">{t('spaces.themeSettings.legend')}</legend>
        {options.map((option) => (
          <label key={option.value} className="app-settings-toggle">
            <input
              type="radio"
              name="space-theme-default"
              value={option.value}
              checked={spaceThemeDefault === option.value}
              onChange={() => chooseDefault(option.value)}
            />
            <span className="app-settings-toggle-label">
              <span className="app-settings-toggle-title">{option.label}</span>
              <span className="app-settings-toggle-hint">{option.hint}</span>
            </span>
          </label>
        ))}
      </fieldset>

      {changed && spacePrefs && (
        <p className="app-settings-section-hint space-themes-changed" role="status">
          {t('spaces.themeSettings.changedHint')}
        </p>
      )}

      {spacePrefs && (
        <div className="space-themes-apply">
          <Button variant="secondary" size="sm" onClick={openConfirm}>
            {t('spaces.themeSettings.applyAll')}
          </Button>
          <span className="app-settings-toggle-hint">{t('spaces.themeSettings.applyAllHint')}</span>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('spaces.themeSettings.confirmTitle')}
        description={confirmBody}
        confirmLabel={t('spaces.themeSettings.confirm')}
        cancelLabel={t('spaces.themeSettings.cancel')}
        onConfirm={() => void applyToAll()}
        loading={applying}
        confirmDisabled={countState.kind === 'loading'}
      />
    </Card>
  );
});
